import type { TutorialInputUsed } from 'Types/EventTypes';
import { DEFAULT_COUNT_IN_BARS } from 'Models/CountIn';

/**
 * Tutorials when the app cannot hear the player (the player's input is not a
 * MIDI keyboard): every exercise objective plays itself, and the Skip button,
 * labelled "Next", moves on.
 *
 * - Timed staff, rhythm and Tap the beat: a 1-bar count-in starts on its own,
 *   the cursor walks the line once with the metronome, nothing is coloured.
 * - Untimed notes: the notes stay on screen, with no cursor.
 * - Find the key: the target key is lit.
 *
 * Then a ring closes around Next and, when it is closed, the tutorial moves on
 * exactly as a tap on Skip does. "Keep practicing" beside Next stops the ring:
 * a timed objective then runs again and again (a count-in between passes)
 * until Next is tapped. No pass animation or feedback clip plays: nothing was
 * heard. A pass the app did hear (tap input) is graded as a keyboard player's
 * is, animation and clip included.
 *
 * The lengths below are first guesses, to be tuned on devices.
 */

/** After the instruction, this long before the first count-in starts. */
export const TUTORIAL_COUNT_IN_START_DELAY_MS = 1000;

/** The Next ring after a timed run (staff, rhythm, Tap the beat). */
export const NEXT_RING_AFTER_TIMED_RUN_MS = 4000;

/** The Next ring on a find-the-key objective. */
export const NEXT_RING_FIND_THE_KEY_MS = 5000;

/** The Next ring on untimed notes: this long per note to play... */
export const NEXT_RING_PER_UNTIMED_NOTE_MS = 2000;

/** ...and never shorter than this. */
export const NEXT_RING_UNTIMED_MIN_MS = 8000;

/** The Next ring on untimed notes with `noteCount` notes (a chord counts once). */
export const untimedNotesRingMs = (noteCount: number): number =>
  Math.max(NEXT_RING_UNTIMED_MIN_MS, Math.max(0, noteCount) * NEXT_RING_PER_UNTIMED_NOTE_MS);

/**
 * How many metronome beats of `beatLengthWholeNotes` the count-in clicks before
 * the first note, for a bar of `barLengthWholeNotes` (one bar; at least one beat).
 */
export const tutorialCountInBeats = (
  barLengthWholeNotes: number,
  beatLengthWholeNotes: number,
): number =>
  Math.max(1, Math.round((barLengthWholeNotes * DEFAULT_COUNT_IN_BARS) / beatLengthWholeNotes));

const INPUT_STRENGTH: Record<TutorialInputUsed, number> = { none: 0, tap: 1, midi: 2 };

/** The stronger of two inputs an exercise was played on: MIDI over taps over nothing. */
export const strongerInput = (
  first: TutorialInputUsed,
  second: TutorialInputUsed,
): TutorialInputUsed => (INPUT_STRENGTH[second] > INPUT_STRENGTH[first] ? second : first);
